import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { LocationProvider } from "@/providers/LocationProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AdminProtectedRoute from "@/components/auth/AdminProtectedRoute";
import { ADMIN_ROUTES, isAdminPath } from "@/lib/admin-routes";
import { GoogleTagManager } from "@/components/GoogleTagManager";
import Index from "./pages/Index";
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import AdminLogin from "./pages/auth/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminHome from "./pages/admin/AdminHome";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminServices from "./pages/admin/AdminServices";
import AdminContractors from "./pages/admin/AdminContractors";
import AdminPromoCodes from "./pages/admin/AdminPromoCodes";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminStatus from "./pages/admin/AdminStatus";
import Dashboard from "./pages/dashboard/Dashboard";
import PastShootings from "./pages/dashboard/PastShootings";
import Profile from "./pages/profile/Profile";
import FilmmakerDashboard from "./pages/filmmaker/Dashboard";
import JobDetails from "./pages/filmmaker/JobDetails";
import PastServices from "./pages/filmmaker/PastServices";
import BookStep1 from "./pages/book/Step1";
import SelectPackage from "./pages/book/SelectPackage";
import BookVerify from "./pages/book/Verify";
import BookPayment from "./pages/book/Payment";
import BookPaymentSuccess from "./pages/book/PaymentSuccess";
import BookConfirmation from "./pages/book/Confirmation";
import TrackBooking from "./pages/track/TrackBooking";
import FindingFilmmaker from "./pages/track/FindingFilmmaker";
import TrackingRouter from "./pages/track/TrackingRouter";
import TestEmail from "./pages/TestEmail";
import NotFound from "./pages/NotFound";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import DesktopRedirect from "@/components/DesktopRedirect";
import { useState, useEffect } from "react";

const queryClient = new QueryClient();

// Rotas legais que devem ser acessíveis no desktop
const LEGAL_ROUTES = ['/privacy-policy', '/terms-and-conditions'];

const isLegalRoute = (path: string): boolean => {
  return LEGAL_ROUTES.includes(path);
};

// Componente que monitora a rota e redireciona usuários desktop quando necessário
const DesktopRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isDesktop && !isAdminPath(location.pathname) && !isLegalRoute(location.pathname)) {
      // Se o usuário está no desktop e não está em uma rota permitida, redireciona para "/"
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
    }
  }, [isDesktop, location.pathname, navigate]);

  // Se estiver no desktop e não for rota permitida, mostra o redirect
  if (isDesktop && !isAdminPath(location.pathname) && !isLegalRoute(location.pathname)) {
    return <DesktopRedirect />;
  }

  return <>{children}</>;
};

const App = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Verificação inicial antes do BrowserRouter
  if (isDesktop && !isAdminPath(window.location.pathname) && !isLegalRoute(window.location.pathname)) {
    return <DesktopRedirect />;
  }

  return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LocationProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <DesktopRouteGuard>
              <GoogleTagManager />
              <Routes>
              <Route path="/" element={<Index />} />
              
              {/* Auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ForgotPassword />} />
              
              {/* Dashboard */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard/history" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <PastShootings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard/profile" 
                element={
                  <ProtectedRoute requiredRole="customer">
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              
              {/* Filmmaker Dashboard */}
              <Route 
                path="/filmmaker" 
                element={
                  <ProtectedRoute requiredRole="filmmaker">
                    <FilmmakerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/filmmaker/jobs/:bookingId" 
                element={
                  <ProtectedRoute requiredRole="filmmaker">
                    <JobDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/filmmaker/past-services" 
                element={
                  <ProtectedRoute requiredRole="filmmaker">
                    <PastServices />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/filmmaker/profile" 
                element={
                  <ProtectedRoute requiredRole="filmmaker">
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              
              {/* Booking Flow */}
              <Route path="/book/step-1" element={<BookStep1 />} />
              <Route path="/book/select-package" element={<SelectPackage />} />
              <Route path="/book/verify" element={<BookVerify />} />
              <Route path="/book/payment" element={<BookPayment />} />
              <Route path="/book/payment-success" element={<BookPaymentSuccess />} />
              <Route path="/book/confirmation" element={<BookConfirmation />} />
              
              {/* Tracking */}
              <Route path="/track/:bookingId" element={<TrackingRouter />} />
              <Route path="/track/:bookingId/finding" element={<FindingFilmmaker />} />
              <Route path="/track/:bookingId/details" element={<TrackBooking />} />
              {/* Legacy routes for backward compatibility */}
              <Route path="/track/:bookingId/step-2" element={<TrackBooking />} />
              <Route 
                path="/track/:bookingId/status" 
                element={
                  <ProtectedRoute>
                    <TrackBooking />
                  </ProtectedRoute>
                } 
              />
              
              {/* Test Pages */}
              <Route path="/test-email" element={<TestEmail />} />
              
              {/* Legal Pages */}
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              
              {/* Dev Pages */}
              
              {/* Admin Routes */}
              <Route path={ADMIN_ROUTES.LOGIN} element={<AdminLogin />} />
              <Route
                path={ADMIN_ROUTES.BASE}
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              >
                <Route index element={<AdminHome />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="contractors" element={<AdminContractors />} />
                <Route path="promo-codes" element={<AdminPromoCodes />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="status" element={<AdminStatus />} />
              </Route>
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </DesktopRouteGuard>
          </BrowserRouter>
        </TooltipProvider>
      </LocationProvider>
    </AuthProvider>
  </QueryClientProvider>
  );
};

export default App;
